import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getSBEmployeeScaleDefinition, fetchGroupJdSkillAnalysis } from "Api/apiService";
import { transformSkillsForProficiency, sortSkillsByImportance } from "Pages/SkillsBridgerV2/HrSkillsBridger/SkillsMap/EmployeeSkillsMapV2.constants";
import {
  MIN_PROFICIENCY,
  MAX_PROFICIENCY,
  DEFAULT_PAGE_SIZE,
  TAB_KEYS,
  ROUTES,
} from "./GroupSkillsProficiencyV2.constants";

// ─── Hook ─────────────────────────────────────────────────────────────────────
//
// Shared by both the employee and group proficiency views.
//
// preloadedSkillAnalysis — raw /employee_jd_skill_analysis  (employee view) or
//                          /group_jd_skill_analysis (group view) response.
//                          When present the hook uses it directly; the data was
//                          fetched once upstream and forwarded via router state.
//
// selectedEmployees — required by the group view so that navigating back to
//                     GroupSkillsMapV2 can carry the same preloaded data along.

const useGroupSkillsProficiency = ({
  viewType              = "group",
  employee              = null,
  selectedEmployees     = [],
  preloadedSkillAnalysis = null,
  groupSummary          = null,
  employeeInsights      = null,
} = {}) => {
  const navigate = useNavigate();

  const [skills,               setSkills]               = useState([]);
  const [scaleData,            setScaleData]            = useState(null);
  const [scaleLoading,         setScaleLoading]         = useState(false);
  const [analysisLoading,      setAnalysisLoading]      = useState(false);
  const [error,                setError]                = useState(null);
  const [activeTab,            setActiveTab]            = useState(TAB_KEYS.SKILLS_BRIDGER);
  const [pendingUpdate,        setPendingUpdate]        = useState(null);
  const [showWarning,          setShowWarning]          = useState(false);
  const [showConditionalAlert, setShowConditionalAlert] = useState(false);
  const [viewScaleModal,       setViewScaleModal]       = useState(false);
  const [currentPage,          setCurrentPage]          = useState(1);
  const [pageSize,             setPageSize]             = useState(DEFAULT_PAGE_SIZE);

  // Holds the resolved analysis — either the preloaded prop or the fetched result.
  const [resolvedAnalysis, setResolvedAnalysis]         = useState(preloadedSkillAnalysis);

  // ── Derived ──────────────────────────────────────────────────────────────────

  const paginatedSkills = skills.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // ── Scale definition ──────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchScaleDefinition = async () => {
      setScaleLoading(true);
      setError(null);
      try {
        const response = await getSBEmployeeScaleDefinition();
        if (response) setScaleData(response);
        else setError("No scale definition found.");
      } catch {
        setError("Failed to fetch scale definition.");
      } finally {
        setScaleLoading(false);
      }
    };
    fetchScaleDefinition();
  }, []);

  // ── Group analysis fetch (group view only, when not preloaded) ────────────────
  // Navigating directly to proficiency passes only selectedEmployees in router
  // state — fetch the analysis here and show the table loader while waiting.

  useEffect(() => {
    if (resolvedAnalysis || viewType !== "group" || !selectedEmployees.length) return;
    setAnalysisLoading(true);
    fetchGroupJdSkillAnalysis(selectedEmployees)
      .then(setResolvedAnalysis)
      .catch((err) => console.error("Group skill analysis fetch error:", err))
      .finally(() => setAnalysisLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Skills — derived from resolved analysis ───────────────────────────────────

  useEffect(() => {
    if (!resolvedAnalysis) return;
    const rawSkills = resolvedAnalysis?.personProfileAnalysis?.skills ?? [];
    const tasks = resolvedAnalysis?.personProfileAnalysis?.tasks ?? [];
    setSkills(transformSkillsForProficiency(sortSkillsByImportance(rawSkills, tasks)));
  }, [resolvedAnalysis]);

  // ── Navigation ────────────────────────────────────────────────────────────────

  const handleBack = useCallback(() => {
    navigate(ROUTES.SKILLS_BRIDGER);
  }, [navigate]);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
  }, []);

  // Passes all resolved data back so the map page never re-fetches.
  const handleNavigateSkillsMap = useCallback(() => {
    if (viewType === "employee") {
      navigate(ROUTES.EMPLOYEE_SKILLS_MAP, {
        state: { employee, employeeSkillAnalysis: resolvedAnalysis, employeeInsights },
      });
      return;
    }
    navigate(ROUTES.GROUP_SKILLS_MAP, {
      state: { selectedEmployees, groupSkillAnalysis: resolvedAnalysis, groupSummary },
    });
  }, [employee, employeeInsights, groupSummary, navigate, resolvedAnalysis, selectedEmployees, viewType]);

  // ── Proficiency change ────────────────────────────────────────────────────────
  // Currently disabled at group level — shows conditional alert instead.

  const handleProficiencyChange = useCallback(
    (index, delta) => {
      if (viewType === "employee") {
        setPendingUpdate({ index, delta });
        setShowWarning(true);
        return;
      }
      setShowConditionalAlert(true);
    },
    [viewType]
  );

  const handleWarningOk = useCallback(() => {
    if (pendingUpdate) {
      setSkills((prev) =>
        prev.map((skill, i) =>
          i === pendingUpdate.index
            ? {
                ...skill,
                required_proficiency: Math.min(
                  MAX_PROFICIENCY,
                  Math.max(MIN_PROFICIENCY, skill.required_proficiency + pendingUpdate.delta)
                ),
              }
            : skill
        )
      );
    }
    setShowWarning(false);
    setPendingUpdate(null);
  }, [pendingUpdate]);

  const handleWarningCancel = useCallback(() => {
    setShowWarning(false);
    setPendingUpdate(null);
  }, []);

  // ── Priority toggle ───────────────────────────────────────────────────────────

  const togglePriority = useCallback((index) => {
    setSkills((prev) =>
      prev.map((skill, i) =>
        i === index ? { ...skill, priority_skill: !skill.priority_skill } : skill
      )
    );
  }, []);

  // ── Pagination ────────────────────────────────────────────────────────────────

  const handlePaginationChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  return {
    skills,
    paginatedSkills,
    scaleData,
    loading: scaleLoading || analysisLoading,
    error,
    activeTab,
    showWarning,
    showConditionalAlert,
    viewScaleModal,
    currentPage,
    pageSize,
    handleBack,
    handleTabChange,
    handleNavigateSkillsMap,
    handleProficiencyChange,
    handleWarningOk,
    handleWarningCancel,
    setShowConditionalAlert,
    setViewScaleModal,
    togglePriority,
    handlePaginationChange,
  };
};

export default useGroupSkillsProficiency;
